import type { IConversationMessage } from '../hooks/useAssistantWebSocket';
import type { SnapshotMessage } from '@orn/global/assistant/snapshotMessage.js';
import type { IWorkItemMoveFacts } from '@orn/global/assistant/workItemMove.js';

export type { SnapshotMessage };

/**
 * Build a fresh `IConversationMessage[]` from a server `transcript_snapshot`.
 *
 * Snapshot-derived messages are never pending (`isPending` omitted) and
 * carry `clientMessageId` directly off the wire row (`undefined` when the
 * server didn't populate one, e.g. eval-path rows).
 *
 * `generateId` is injected so callers can stub it in tests and the hook can
 * pass its own time+random implementation in production.
 *
 * The first `priorThreadMessageCount` rows come from earlier threads and are
 * marked `isPriorThread`. They convert apart from the rest so a tool-call line
 * never collapses across the thread boundary.
 */
export function snapshotToConversation(
  snapshot: SnapshotMessage[],
  generateId: () => string,
  priorThreadMessageCount = 0
): IConversationMessage[] {
  const priorThread = convertSnapshotRows(snapshot.slice(0, priorThreadMessageCount), generateId).map(
    (message): IConversationMessage => ({ ...message, isPriorThread: true })
  );
  return [...priorThread, ...convertSnapshotRows(snapshot.slice(priorThreadMessageCount), generateId)];
}

function convertSnapshotRows(snapshot: SnapshotMessage[], generateId: () => string): IConversationMessage[] {
  const out: IConversationMessage[] = [];
  for (const row of snapshot) {
    switch (row.kind) {
      case 'user':
        out.push({
          id: `msg-${generateId()}`,
          role: 'user',
          content: row.content,
          timestamp: new Date(row.timestamp),
          clientMessageId: row.clientMessageId,
          turnKey: row.turnId,
          ...(row.attachments && row.attachments.length > 0 ? { attachments: row.attachments } : {}),
        });
        break;
      case 'assistant':
        out.push({
          id: `msg-${generateId()}`,
          role: 'assistant',
          content: row.content,
          timestamp: new Date(row.timestamp),
          turnKey: row.turnId,
        });
        break;
      case 'tool_call': {
        // Collapse consecutive identical goals (parallel calls to the same tool —
        // e.g. one `math_resolve` per category) into a single gray line. Mirrors
        // the live `insertToolCall` dedup so snapshot and live render the same.
        const last = out[out.length - 1];
        if (last?.kind === 'tool-call' && last.content === row.goal) {
          out[out.length - 1] = absorbToolCall(last, row.toolId, row.workItemMove);
          break;
        }
        out.push({
          id: `tool-call-${row.toolId}`,
          role: 'assistant',
          kind: 'tool-call',
          toolId: row.toolId,
          content: row.goal,
          timestamp: new Date(row.timestamp),
          turnKey: row.turnId,
          ...(row.workItemMove === undefined ? {} : { workItemMoves: [row.workItemMove] }),
        });
        break;
      }
      case 'alert_card':
        // The alert an expand turn was opened on. Every string it shows is read
        // from the live work item at render, so `content` stays deliberately
        // empty — which means a consumer that judges rows by `content` has to
        // recognize this `kind` rather than read the row as blank.
        out.push({
          id: `alert-card-${generateId()}`,
          role: 'assistant',
          kind: 'alert-card',
          alertId: row.alertId,
          content: '',
          timestamp: new Date(row.timestamp),
          turnKey: row.turnId,
        });
        break;
      default: {
        // Compile-time exhaustiveness check: adding a new `SnapshotMessage`
        // variant without a case branch fails here.
        const _exhaustive: never = row;
        void _exhaustive;
      }
    }
  }
  return out;
}

/**
 * Reconcile prior in-memory messages against a server-authoritative
 * `transcript_snapshot`: replace the list with the snapshot, then re-append
 * any pending user bubble (`isPending: true`) whose `clientMessageId` does
 * not appear on a `snapshot.user` row.
 *
 * Identity match by `clientMessageId` is the only confirmation path —
 * production web clients always populate it, and the snapshot reconciler
 * is only ever exercised against real server snapshots.
 *
 * Non-pending bubbles defer entirely to the snapshot — the server is
 * authoritative for already-confirmed history. The caller passes the raw
 * `SnapshotMessage[]` alongside the converted list so this reconciler can
 * read `clientMessageId` directly off the wire row without re-deriving.
 */
export function reconcileSnapshotMessages(
  prev: IConversationMessage[],
  snapshotMessages: IConversationMessage[],
  snapshotRaw: SnapshotMessage[]
): IConversationMessage[] {
  const seenIds = confirmedUserMessageIds(snapshotRaw);
  const preserved = prev.filter(
    (m) => m.isPending === true && (m.clientMessageId === undefined || !seenIds.has(m.clientMessageId))
  );
  return [...snapshotMessages, ...preserved];
}

/**
 * The `clientMessageId`s a snapshot proves the server has persisted.
 *
 * The transcript reconciler drops a pending bubble whose id is in here, and the
 * outbound queue drops a frame whose id is in here — one definition, because a
 * bubble the snapshot confirms and a frame the snapshot confirms are the same
 * fact, and letting the two disagree is how a turn gets sent twice.
 */
export function confirmedUserMessageIds(snapshotRaw: SnapshotMessage[]): Set<string> {
  const ids = new Set<string>();
  for (const row of snapshotRaw) {
    if (row.kind === 'user' && row.clientMessageId !== undefined) {
      ids.add(row.clientMessageId);
    }
  }
  return ids;
}

/**
 * Insert the session-opening welcome row at its place in time. The row is
 * client-local (never a snapshot row), so every snapshot replacement evicts it
 * and the hook re-inserts it from the snapshot's `welcome` — this is the one
 * insertion rule both the first insert and every re-insert share.
 *
 * The slot is the first row that is either pending (an optimistic bubble whose
 * turn has not run yet — it belongs below the greeting whatever its typed-at
 * time says) or timestamped after the welcome. On a fresh bind that is the end
 * of the rehydrated history; after a reconnect or reload mid-session it lands
 * the row back above the turns that followed it.
 *
 * Returns a NEW array. Pure — no React, fully unit-testable.
 */
export function insertWelcomeRow(
  messages: IConversationMessage[],
  welcome: IConversationMessage
): IConversationMessage[] {
  const welcomeMs = welcome.timestamp.getTime();
  const i = messages.findIndex((m) => m.isPending === true || m.timestamp.getTime() > welcomeMs);
  const at = i === -1 ? messages.length : i;
  return [...messages.slice(0, at), welcome, ...messages.slice(at)];
}

/**
 * Confirm every pending bubble whose `clientMessageId` appears in
 * `clientMessageIds`. Used when the server replies to a turn — the
 * `assistant_message` echoes the full id array (one for single-message
 * turns, N for batched turns) and all matching bubbles flip from pending
 * to confirmed in a single pass.
 *
 * Returns a NEW array. Bubbles whose ids are not in the set, and non-user /
 * already-confirmed bubbles, are left untouched. Empty `clientMessageIds`
 * (eval-path turn without ids) returns the input unchanged.
 */
export function confirmPendingMessages(
  messages: IConversationMessage[],
  clientMessageIds: string[]
): IConversationMessage[] {
  if (clientMessageIds.length === 0) {
    return messages;
  }
  const idSet = new Set(clientMessageIds);
  let changed = false;
  const next = messages.map((m) => {
    if (m.role === 'user' && m.isPending === true && m.clientMessageId !== undefined && idSet.has(m.clientMessageId)) {
      changed = true;
      return { ...m, isPending: false };
    }
    return m;
  });
  return changed ? next : messages;
}

/**
 * Index just past the in-flight turn's items — the position of the first
 * *queued* user bubble (`isPending && !isInFlight`), or `messages.length` when
 * none is queued. Queued bubbles are always the trailing run (you can only
 * queue a message while a turn is already in flight), so this index is the
 * boundary between "items belonging to the current turn" and "what the user
 * typed to send next."
 */
function inFlightSectionEnd(messages: IConversationMessage[]): number {
  const firstQueued = messages.findIndex((m) => m.isPending === true && m.isInFlight !== true);
  return firstQueued === -1 ? messages.length : firstQueued;
}

function insertAtInFlightSectionEnd(
  messages: IConversationMessage[],
  item: IConversationMessage
): IConversationMessage[] {
  const i = inFlightSectionEnd(messages);
  return [...messages.slice(0, i), item, ...messages.slice(i)];
}

/**
 * Insert an assistant reply bubble at the end of the in-flight section (see
 * {@link inFlightSectionEnd}). The stream-from-the-server protocol emits N
 * `assistant_message` frames per turn (one per round with prose); each appends
 * after the prior in-flight items — the in-flight user message(s), earlier
 * rounds' bubbles, and any tool-call lines — and above any bubble the user
 * queued mid-turn. Arrival order in, arrival order out: a turn that runs tools
 * then answers renders `user → goal… → goal… → answer`.
 *
 * Returns a NEW array. Pure function — no React, no refs, fully unit-testable.
 */
export function insertAssistantReply(
  messages: IConversationMessage[],
  reply: IConversationMessage
): IConversationMessage[] {
  return insertAtInFlightSectionEnd(messages, reply);
}

/**
 * Insert a tool-call line at the same in-flight-section slot as a reply, so
 * live tool-call lines and the answer they precede render in arrival order (tool-call lines first).
 *
 * Returns a NEW array. Pure — no React, fully unit-testable.
 */
export function insertToolCall(messages: IConversationMessage[], goal: IConversationMessage): IConversationMessage[] {
  // Collapse parallel calls to the same tool: a batch of identical goals (e.g.
  // one `math_resolve` per category) arrives as consecutive `tool_started`
  // frames — render a single gray line instead of N copies. Only the line
  // immediately preceding the insertion slot is checked, so a genuinely later
  // round that repeats the goal (after a reply bubble) still shows its own line.
  const end = inFlightSectionEnd(messages);
  const preceding = end > 0 ? messages[end - 1] : undefined;
  if (preceding?.kind === 'tool-call' && preceding.content === goal.content && goal.toolId !== undefined) {
    const next = [...messages];
    next[end - 1] = absorbToolCall(preceding, goal.toolId);
    return next;
  }
  return insertAtInFlightSectionEnd(messages, goal);
}

/**
 * Fold a collapsed call into the line that renders for it: its id, so a later
 * frame naming that call still finds the line, and the move facts its result
 * carried, if the snapshot already knows them.
 */
function absorbToolCall(
  line: IConversationMessage,
  toolId: string,
  workItemMove?: IWorkItemMoveFacts
): IConversationMessage {
  return {
    ...line,
    absorbedToolIds: [...(line.absorbedToolIds ?? []), toolId],
    ...(workItemMove === undefined ? {} : { workItemMoves: upsertMove(line.workItemMoves, workItemMove) }),
  };
}

/**
 * Last write wins per item: two calls on one collapsed line that both moved
 * the same item must freeze the later after-state, not the first.
 */
function upsertMove(list: IWorkItemMoveFacts[] | undefined, fact: IWorkItemMoveFacts): IWorkItemMoveFacts[] {
  return [...(list ?? []).filter((f) => f.workItemId !== fact.workItemId), fact];
}

/**
 * Record on a tool-call line what one of its calls left a work item as, once
 * the server reports the write succeeded. The line is found by the call's own
 * id or by an id it absorbed when identical goals collapsed; a call with no
 * line at all (it carried no goal) stamps nothing.
 *
 * @param messages - the transcript; every untouched row keeps its identity.
 * @param toolId - the framework call id the `tool_completed` frame named.
 * @param workItemMove - the frozen facts of what that call left the item as.
 * @returns a new array with the one line replaced, or `messages` itself when
 *   no line renders for the call.
 */
export function stampToolCallWorkItemMove(
  messages: IConversationMessage[],
  toolId: string,
  workItemMove: IWorkItemMoveFacts
): IConversationMessage[] {
  const idx = messages.findIndex(
    (m) => m.kind === 'tool-call' && (m.toolId === toolId || m.absorbedToolIds?.includes(toolId) === true)
  );
  if (idx < 0) {
    return messages;
  }
  const next = [...messages];
  next[idx] = { ...messages[idx], workItemMoves: upsertMove(messages[idx].workItemMoves, workItemMove) };
  return next;
}

// ============================================
// Streaming-bubble lifecycle
// ============================================
//
// Token-level streaming grows one assistant bubble in place across a round's
// `assistant_message_delta` frames, then snaps it to the authoritative text on
// the terminal `assistant_message`. The bubble is seeded by `insertAssistantReply`
// (above) carrying `isStreaming: true`, grown by `appendStreamingDelta`, and then
// either finalized by `finalizeStreamingBubble` (success) or swept up by
// `dropMessagesByIds` (error / abort mid-stream), which retracts the whole
// turn's rows rather than the bubble alone — a turn the transcript never took
// leaves none of them behind. All of these return a NEW array and preserve the
// object identity of every untouched row, so a memoized row list re-renders
// only what changed.

/**
 * Append a token-level delta to the streaming bubble, concatenating onto its
 * running text. A no-op (input contents unchanged) when no row matches —
 * defensive against a stray delta after the bubble was dropped.
 */
export function appendStreamingDelta(
  messages: IConversationMessage[],
  streamingId: string,
  text: string
): IConversationMessage[] {
  return messages.map((m) => (m.id === streamingId ? { ...m, content: m.content + text } : m));
}

/**
 * Snap the streaming bubble to the round's authoritative full text and clear its
 * `isStreaming` flag. Replacing (not appending) the content drops any client-side
 * accumulation drift between the streamed deltas and the server's canonical text.
 */
export function finalizeStreamingBubble(
  messages: IConversationMessage[],
  streamingId: string,
  content: string
): IConversationMessage[] {
  return messages.map((m) => {
    if (m.id !== streamingId) {
      return m;
    }
    const finalized = { ...m, content };
    delete finalized.isStreaming;
    return finalized;
  });
}

/**
 * Remove every row a turn left behind when the turn itself never reached the
 * transcript — it errored, was aborted, or completed `discarded`. The server
 * elides such a turn wholesale, so retracting a strict subset would trade one
 * mismatch for another: the point is that live and rehydrated agree.
 *
 * Tolerant of ids that are no longer present (a snapshot may already have
 * replaced the list), and of an empty set.
 */
export function dropMessagesByIds(messages: IConversationMessage[], ids: readonly string[]): IConversationMessage[] {
  if (ids.length === 0) {
    return messages;
  }
  const doomed = new Set(ids);
  return messages.filter((m) => !doomed.has(m.id));
}
